import type { z } from "zod";
import { nestedCtaSchema } from "./schemas";
import {
  NESTED_CTA_TYPE_DISPLAY_MESSAGE,
  NESTED_CTA_TYPE_GO_TO_PAGE,
  isNestedCtaType,
} from "@/types/nestedCtaType";

type NestedCta = z.output<typeof nestedCtaSchema>;

export type CtaRedirectSource = {
  redirectTo?: string | undefined;
  redirectToService?:
    | Record<string, string>
    | { name: string; specialization: string }
    | null
    | undefined;
};

export type CtaFieldSink = {
  ctaKeys: string[];
  ctaTexts: string[];
  redirectTos: string[];
  redirectServiceSnippets: string[];
};

const pushNonEmpty = (out: string[], value: string | undefined): void => {
  if (value != null && value.length > 0) {
    out.push(value);
  }
};

export const appendCtaKeyAndText = (
  sink: CtaFieldSink,
  key: string,
  text: string,
): void => {
  pushNonEmpty(sink.ctaKeys, key);
  pushNonEmpty(sink.ctaTexts, text);
};

export const appendRedirectFields = (
  sink: CtaFieldSink,
  source: CtaRedirectSource,
): void => {
  if (source.redirectTo !== undefined) {
    const r = source.redirectTo.trim();
    pushNonEmpty(sink.redirectTos, r);
  }
  const svc = source.redirectToService;
  if (svc != null) {
    const snip = [svc.name, svc.specialization].filter(Boolean).join(" ");
    pushNonEmpty(sink.redirectServiceSnippets, snip);
  }
};

export const collectNestedCtaFields = (
  sink: CtaFieldSink,
  cta: NestedCta,
): void => {
  if (!isNestedCtaType(cta.type)) {
    return;
  }
  appendCtaKeyAndText(sink, cta.key, cta.text);
  switch (cta.type) {
    case NESTED_CTA_TYPE_GO_TO_PAGE:
      appendRedirectFields(sink, cta);
      break;
    case NESTED_CTA_TYPE_DISPLAY_MESSAGE:
      if (cta.messages != null) {
        for (const message of cta.messages) {
          pushNonEmpty(sink.ctaTexts, message);
        }
      }
      break;
    default: {
      const _exhaustive: never = cta;
      return _exhaustive;
    }
  }
};
